/**
 * The `/admin/automation/*` route table (A7Y-3802), declared here rather than
 * inline in `App.tsx` so that `automationRoutes.test.tsx` can run react-router's
 * own matcher over the REAL array — a route that is deleted, misspelled, or
 * shadowed then fails a test instead of failing silently in the browser.
 *
 * MEASURED CORRECTION (A7Y-3802): declaration order is NOT what decides this.
 * react-router 7 ranks matches by specificity — a literal segment outscores a
 * dynamic one — so `automation/quota/:account` beats `automation/:kind/:stamp`
 * for `/automation/quota/ops01` wherever it sits in this array. The ticket, and
 * the two pre-existing comments below that this file inherited from `App.tsx`,
 * both stated the opposite; swapping the two routes was tried and the matcher
 * was unmoved. The literal-first order is kept because it reads correctly and
 * costs nothing, but it is a convention here, not a mechanism, and the test
 * asserts the resolution rather than the ordering.
 *
 * Rendered as `{automationRoutes}` inside `<Routes>` — a `<Routes>` element
 * flattens array children, so these are the same `<Route>` elements they were,
 * with the same paths relative to the `/admin` layout route.
 */
import { Navigate, Route } from 'react-router-dom';
import AddisStatusPage from '@/pages/autobuild/OverviewPage';
import AddisOverviewPage from '@/pages/autobuild/AddisOverviewPage';
import QuotaPage from '@/pages/autobuild/QuotaPage';
import CdidPage from '@/pages/autobuild/CdidPage';
import AdgsPage from '@/pages/autobuild/AdgsPage';
import { ApdPage } from '@/pages/autobuild/RoutinePage';
import AutobuildSessionDetailPage from '@/pages/autobuild/SessionDetailPage';
import WorkloadsPage from '@/pages/autobuild/WorkloadsPage';
import SlotsPage from '@/pages/autobuild/SlotsPage';
import QuotaAccountDetailPage from '@/pages/autobuild/QuotaAccountDetailPage';

export const automationRoutes = [
  <Route key="automation" path="automation" element={<AddisStatusPage />} />,
  /* Is each system currently ON — one literal segment, so it cannot be
     captured by `automation/:kind/:stamp`, which is two. */
  <Route key="automation-overview" path="automation/overview" element={<AddisOverviewPage />} />,
  /* `quotas` (plural) is the account TABLE; `quota/:account` below is one
     account's detail. Two literal segments, so neither can shadow the other
     nor be shadowed by `:kind` — asserted in automationRoutes.test.tsx. */
  <Route key="automation-quotas" path="automation/quotas" element={<QuotaPage />} />,
  <Route key="automation-cdid" path="automation/cdid" element={<CdidPage />} />,
  <Route key="automation-adgs" path="automation/adgs" element={<AdgsPage />} />,
  /* AHC's PAGE moved to `platform-health/ahc` (TPF, 2026-09-09) — the nightly
     health check belongs with platform health, not with the two pipelines. The
     redirect keeps existing links working, and `automation/ahc/:stamp` below is
     untouched: that is the SESSION detail route, where `ahc` is a kind rather
     than a page. */
  <Route
    key="automation-ahc"
    path="automation/ahc"
    element={<Navigate to="/admin/platform-health/ahc" replace />}
  />,
  <Route key="automation-apd" path="automation/apd" element={<ApdPage />} />,
  /* Kept ahead of automation/:kind/:stamp by convention. The literal segment
     already outranks the dynamic one — see the correction above. */
  <Route key="automation-workloads" path="automation/workloads" element={<WorkloadsPage />} />,
  /* Same convention as workloads above. */
  <Route key="automation-slots" path="automation/slots" element={<SlotsPage />} />,
  /* Same two-segment shape as the session route below; the literal `quota`
     segment is what wins the match, asserted in automationRoutes.test.tsx. */
  <Route
    key="automation-quota-account"
    path="automation/quota/:account"
    element={<QuotaAccountDetailPage />}
  />,
  <Route
    key="automation-session"
    path="automation/:kind/:stamp"
    element={<AutobuildSessionDetailPage />}
  />,
];
